// src/pages/creator-panel/Connector/GoogleReviews/EditGoogleReview.js
import React, { useState } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import {
    Box, Typography, IconButton, TextField, Button as MuiButton, CircularProgress, Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function EditGoogleReview({ review, chatbotId, permission, onClose, onUpdate }) {
    const [pageContent, setPageContent] = useState(review.page_content || "");
    const [summary, setSummary] = useState(review.summary || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const canModify = Boolean(permission?.canModify);
    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    const handleSave = async () => {
        if (!pageContent.trim()) {
            setError("Content cannot be empty.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await axiosInstance.post(
                `/api/google-reviews/update-review-content/${review.id}`,
                { page_content: pageContent, summary },
                { params }
            );
            onUpdate();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to update review.");
            setSaving(false);
        }
    };

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="h6">Edit Review Content</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Reviewer: <strong>{review.reviewer_display_name || "Anonymous"}</strong> &nbsp;|&nbsp;
                    Rating: <strong>{review.star_rating || 0}/5</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Editing this content marks the review as manually edited. Saving re-trains it for the chatbot,
                    and future syncs from Google will ask before overwriting your edit.
                </Typography>
                <TextField
                    label="Embedded Content (used for chatbot retrieval)"
                    multiline minRows={8} fullWidth
                    value={pageContent}
                    onChange={(e) => setPageContent(e.target.value)}
                    disabled={!canModify || saving}
                    sx={{ mt: 2 }}
                />
                <TextField
                    label="Summary"
                    multiline minRows={3} fullWidth
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    disabled={!canModify || saving}
                    sx={{ mt: 2 }}
                />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <MuiButton onClick={onClose} color="inherit" disabled={saving}>Cancel</MuiButton>
                <MuiButton
                    onClick={handleSave}
                    variant="contained"
                    disabled={!canModify || saving}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {saving ? "Saving..." : "Save & Re-train"}
                </MuiButton>
            </Box>
        </>
    );
}
